import { ICON_MAPPING, getIconComponent } from "@storytell/bsky-icons";
import { DisassociateLabelRequestConfig, useStorytellClient } from "@storytell/client";
import { useCallOptions, useLabelClient, withBearerToken } from "@storytell/cp-client";
import { dispatchGlobalEvent } from "@storytell/events";
import type { AssetId, Label, LabelId } from "@storytell/types";
import { StButton } from "@storytell/ui/buttons";
import { StPopover, StPopoverTrigger } from "@storytell/ui/popover";
import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import { TbPlus, TbTags, TbX } from "solid-icons/tb";
import { type Component, For, Show, createSignal } from "solid-js";
import { Dynamic } from "solid-js/web";
import { twMerge } from "tailwind-merge";
import { truncateMiddle } from "~/utils/truncateMiddle";
import { useWire } from "~/wire";
import { isLightColor } from "../layouts/minimal/isLightColor";
import { AssetLabelPicker } from "./AssetLabelPicker";
import { BulkAssetLabelPicker } from "./BulkAssetLabelPicker";
import { LabelTooltip } from "./LabelTooltip";

export const AssetLabelsRow: Component<{
  assetId: AssetId;
  mount: Node | undefined;
  setSearch: (s: string) => void;
  addFilter: (s: string) => void;
  selectedAssets?: string[];
}> = (props) => {
  const wire = useWire();

  const [labelPickerOpen, setLabelPickerOpen] = createSignal(false);
  const [expanded, setExpanded] = createSignal(false);
  const assetLabels = () =>
    wire.rels
      .labelsForAsset(props.assetId)
      .map((l) => wire.labels.get(l))
      .filter(Boolean)
      .toSorted((a, b) => a?.name.localeCompare(b?.name || "") ?? 0) as Label[];

  const clamped = () => (assetLabels().length <= 2 ? assetLabels() : assetLabels().slice(0, 1));
  const rest = () => (assetLabels().length <= 2 ? [] : assetLabels().slice(1));

  const onRemoveLabelFromAsset = async (labelId: string) => {
    const client = useStorytellClient();
    const existing = wire.rels.assetHasLabel(props.assetId, labelId as LabelId);
    if (!existing) return;

    wire.rels.removeLabelFromAsset(props.assetId, labelId as LabelId);
    try {
      const request = DisassociateLabelRequestConfig.withBearerToken(wire.auth.token());

      const associationId = wire.labels.getAssociation(labelId, props.assetId)?.id;
      if (!associationId) return;
      const result = await client.fetch(request, undefined, [
        {
          associationId,
        },
      ]);
      if (result.isErr()) {
        wire.rels.addLabelToAsset(props.assetId, labelId as LabelId);
      }

      wire.labels.onLabelDisassociated(associationId);
    } catch (error) {
      console.error(error);
      wire.rels.addLabelToAsset(props.assetId, labelId as LabelId);
    }
  };

  return (
    <div
      class="flex items-center gap-1.5 relative"
      classList={{
        "flex-wrap": expanded(),
      }}
    >
      <Show when={wire.grants.canUpdate("cp.label_associations")}>
        <StPopover
          noArrow
          gutter={10}
          placement="bottom-start"
          portalProps={{
            mount: props.mount,
          }}
          contentProps={{
            class: "z-50",
          }}
          open={labelPickerOpen()}
          onOpenChange={setLabelPickerOpen}
          content={
            <Show
              when={(props.selectedAssets?.length ?? 0) > 1}
              fallback={
                <AssetLabelPicker
                  assetId={props.assetId}
                  assetLabels={assetLabels()}
                  mount={props.mount}
                  onLabelCreated={async () => {
                    setLabelPickerOpen(false);
                    dispatchGlobalEvent("refresh-sync", undefined);
                  }}
                  onClose={() => setLabelPickerOpen(false)}
                />
              }
            >
              <BulkAssetLabelPicker
                onLabelCreated={() => {
                  setLabelPickerOpen(false);
                  dispatchGlobalEvent("refresh-sync", undefined);
                }}
                onClose={() => setLabelPickerOpen(false)}
                mount={props.mount}
                selected={props.selectedAssets as AssetId[]}
              />
            </Show>
          }
        >
          <StPopoverTrigger as={StButton} class="group">
            <span class="group-hover:hidden">+</span>
            <span class="hidden group-hover:inline">+ Add a Label</span>
          </StPopoverTrigger>
        </StPopover>
      </Show>
      <For each={expanded() ? assetLabels() : clamped()}>
        {(label) => (
          <StTooltip
            placement="top"
            portalProps={{
              mount: props.mount,
            }}
            contentProps={{
              class: "z-50",
            }}
            content={<LabelTooltip labelId={label.id} />}
          >
            <StTooltipTrigger
              as={StButton}
              onClick={() => {
                props.addFilter(label.id);
              }}
              // biome-ignore lint/suspicious/noExplicitAny: <explanation>
              icon={(props: any) => (
                <Show
                  when={wire.grants.canUpdate("cp.label_associations")}
                  fallback={<Dynamic component={getIconComponent(label.icon)} {...props} />}
                >
                  <Dynamic
                    component={getIconComponent(label.icon)}
                    {...props}
                    class={twMerge(props.class, "group-hover/label:hidden")}
                  />
                  <TbX
                    {...props}
                    class={twMerge(props.class, "hidden group-hover/label:block")}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopImmediatePropagation();
                      onRemoveLabelFromAsset(label.id);
                    }}
                  />
                </Show>
              )}
              class="p-1 shrink-0 whitespace-nowrap group/label bg-tile hover:bg-tile-hover"
              iconClass="size-4 p-0.5 rounded-full bg-[var(--label-color)] text-[var(--label-icon)]"
              style={{
                "--label-color": label.color,
                "--label-icon": isLightColor(label.color) ? "black" : "white",
              }}
            >
              {truncateMiddle(label.name)}
            </StTooltipTrigger>
          </StTooltip>
        )}
      </For>
      <Show when={rest().length}>
        <StButton
          class="p-1 shrink-0 whitespace-nowrap"
          iconClass="size-3.5"
          icon={TbTags}
          onClick={() => setExpanded((s) => !s)}
        >
          {expanded() ? "Hide" : `${rest().length} More`}
        </StButton>
      </Show>
    </div>
  );
};
